import { on, select, addClass, removeClass, getHeight, isIosDevice, hasClass, setAttribute, ready, toggleClass, selectAll } from 'helpers/dom'
import throttle from 'lodash.throttle'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { setCookie } from '@convertdigital/gamma-cookie'

const selectors = {
  THROTTLE_TIME: 50,
  TOP_BAR_SELECTOR: '.js-topbar',
  PINNED_CLASS: 'is-pin',
  UNPINNED_CLASS: 'is-unpin',
  ACTIVE_CLASS: 'is-active',
  MAIN_CONTENT_SELECTOR: '.js-main-content',
  MAIN_CONTENT_NORMAL_STATE: 'is-normal',
  PAGE_HEADER_SELECTOR: '.js-page-header',
  ACCOUNT_MENU_SELECTOR: '.js-account-dropdown',
  ACCOUNT_MENU_OVERLAY: '.js-account-overlay',
  SEARCH_BUTTON_SELECTOR: '.js-search-button',
  SEARCH_BAR_SELECTOR: '.js-search-bar',
  SEARCH_BAR_INPUT_SELECTOR: '.js-search-bar-input',
  ACCOUTN_HEADER_SELECTOR: '.js-account-header',
  logoutSelector: '[href="/account/logout"]'
}

class SiteHeader extends HTMLElement {
  constructor() {
    super()

    this.pageHeaderEl = select(selectors.PAGE_HEADER_SELECTOR)
    this.topBarEl = select(selectors.TOP_BAR_SELECTOR)
    this.accountMenuEl = select(selectors.ACCOUNT_MENU_SELECTOR)
    this.mainContentEl = select(selectors.MAIN_CONTENT_SELECTOR)
    this.accountMenuOverlay = select(selectors.ACCOUNT_MENU_OVERLAY)
    this.searchButtonEl = select(selectors.SEARCH_BUTTON_SELECTOR)
    this.searchBarEl = select(selectors.SEARCH_BAR_SELECTOR)
    this.pageHeaderHeight = getHeight(this.pageHeaderEl)
    this.topBarHeight = getHeight(this.topBarEl)
    this.accountHeader = select(selectors.ACCOUTN_HEADER_SELECTOR)
    this.logoutButton = select(selectors.logoutSelector, this)
    this.lastScrollY = 0
    this.currentScrollY = 0
    this.ticking = false
    this.setAttributeStyle()
    this.showSuccessLogin()
    on(
      'scroll',
      throttle(() => {
        this.handleScroll()
      }, selectors.THROTTLE_TIME),
      window
    )
    on(
      'resize',
      throttle(() => {
        this.pageHeaderHeight = getHeight(this.pageHeaderEl)
        this.setAttributeStyle()
      }, selectors.THROTTLE_TIME),
      window
    )
    ready(() => {
      this.handleReady()
    })

    if (isIosDevice) {
      addClass('ios-device', document.body)
    }

    if (this.accountMenuEl) {
      this.accountMenuEl.addEventListener('click', () => {
        toggleClass(selectors.ACTIVE_CLASS, this.accountMenuEl)
      })
    }

    on('click', () => {
      if (hasClass(selectors.ACTIVE_CLASS, this.accountMenuEl)) {
        removeClass(selectors.ACTIVE_CLASS, this.accountMenuEl)
        toggleClass(selectors.ACTIVE_CLASS, this.accountMenuEl)
      }
    }, this.accountMenuOverlay)

    on('click', () => {
      const searchBarInputList = selectAll(selectors.SEARCH_BAR_INPUT_SELECTOR)
      toggleClass(selectors.ACTIVE_CLASS, this.searchBarEl)
      setTimeout(() => {
        if (!this.searchBarEl.classList.contains(selectors.ACTIVE_CLASS)) {
          searchBarInputList.forEach(searchBarInputItem => searchBarInputItem.focus())
          globalEmitter.emit(emitterProps.search.closeDropdown)
        }
      }, 200)
    }, this.searchButtonEl)

    this.accountHeader && on('click', (e) => {
      if (!window.location.pathname.includes('account')) {
        e.preventDefault()
        window.location.href = this.accountHeader.getAttribute('href')
      }
    }, this.accountHeader)

    this.logoutButton && on('click', (e) => {
      e.preventDefault()
      setCookie('customerAccessToken', '', -999)

      window.location.href = '/account/logout'
    }, this.logoutButton)
  }

  handleScroll() {
    this.currentScrollY = window.pageYOffset
    if (!this.ticking) {
      requestAnimationFrame(this.updateScroll.bind(this))
    }
    this.ticking = true
  }

  handleReady() {
    if (window.pageYOffset > this.pageHeaderEl) {
      addClass(selectors.PINNED_CLASS, this.pageHeaderEl)
    }
  }

  updateScroll() {
    if (this.currentScrollY <= this.pageHeaderHeight) {
      removeClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)
      removeClass(selectors.PINNED_CLASS, this.pageHeaderEl)
    } else {
      this.handleUpdateScroll()
    }

    this.lastScrollY = this.currentScrollY
    this.ticking = false
  }

  handleUpdateScroll() {
    if (this.currentScrollY < this.lastScrollY - 10) {
      this.pin()
    } else if (this.currentScrollY > this.lastScrollY + 15) {
      this.unpin()
    }
  }

  pin() {
    if (hasClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)) {
      removeClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)
      addClass(selectors.PINNED_CLASS, this.pageHeaderEl)
    }
  }

  unpin() {
    if (hasClass(selectors.PINNED_CLASS, this.pageHeaderEl) || !hasClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)) {
      removeClass(selectors.PINNED_CLASS, this.pageHeaderEl)
      addClass(selectors.UNPINNED_CLASS, this.pageHeaderEl)
    }
  }

  setAttributeStyle() {
    this.topBarHeight = getHeight(this.topBarEl)
    const isMobile = window.innerWidth < 768

    let hiddenBarHeight = this.topBarHeight
    if (isMobile) {
      const postcodeWrappers = selectAll('[data-postcode-wrapper]')
      const visibleWrapper = Array.from(postcodeWrappers).find(el => el.offsetHeight > 0)
      if (visibleWrapper) {
        hiddenBarHeight += getHeight(visibleWrapper)
      }
    }

    const paddingTop = this.pageHeaderHeight
    setAttribute('style', `--topbar-height: ${hiddenBarHeight}px;`, this.pageHeaderEl)
    if (hasClass(selectors.MAIN_CONTENT_NORMAL_STATE, this.mainContentEl)) {
      setAttribute('style', `padding-top: ${paddingTop}px`, this.mainContentEl)
    }
    document.documentElement.style.setProperty('--page-header-height', `${paddingTop}px`)
  }

  showSuccessLogin() {
    customElements.whenDefined('notice-global').then(() => {
      if (window.location.search.includes('success')) {
        setTimeout(() => {
          globalEmitter.emit(emitterProps.notice.global, {
            type: 'success',
            content: "You've successfully signed in"
          })

          const urlParams = new URL(window.location.href)
          urlParams.search = ''
          window.history.replaceState({}, '', urlParams)
        }, 200)
      }
    })
  }
}
customElements.define('site-header', SiteHeader)
